import React, { useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, ChevronRight, Info, LayoutTemplate, Check } from 'lucide-react';
import type { SectionTemplateKey } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import { BUILDER_TEMPLATES, SectionCategory } from '../builderTemplates';
import styles from './TemplatePanel.module.scss';

interface TemplatePanelProps {
  onAddSection: (type: SectionTemplateKey) => void;
  pageBackgroundUrl: string;
  onChangePageBackgroundUrl: (url: string) => void;
  companyDomain: string;
  onChangeCompanyDomain: (domain: string) => void;
  domainAvailability: 'idle' | 'checking' | 'available' | 'unavailable';
  domainValidationMessage?: string;
  domainReady: boolean;
  hideTemplateKeys?: SectionTemplateKey[];
  /** Collapse the entire primary (left) sidebar — same idea as Active Sections hide */
  onRequestHide?: () => void;
}

function TemplatePanel({
  onAddSection,
  pageBackgroundUrl,
  onChangePageBackgroundUrl,
  companyDomain,
  onChangeCompanyDomain,
  domainAvailability,
  domainValidationMessage,
  domainReady,
  hideTemplateKeys = [],
  onRequestHide,
}: TemplatePanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['Navigation', 'Main Content']),
  );
  const toggleCategory = (cat: string) => {
    const next = new Set(expandedCategories);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setExpandedCategories(next);
  };

  const templatesByCategory = useMemo(() => {
    const groups: Record<SectionCategory, typeof BUILDER_TEMPLATES> = {
      Navigation: [],
      'Main Content': [],
      'Gallery & Media': [],
      Connect: [],
      Other: [],
    };
    for (const t of BUILDER_TEMPLATES) {
      if (hideTemplateKeys.includes(t.key)) continue;
      groups[t.category].push(t);
    }
    return groups;
  }, [hideTemplateKeys]);

  const handleBackgroundFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImageFile(file)
      .then((imageUrl) => onChangePageBackgroundUrl(imageUrl))
      .catch(() => {});
    e.target.value = '';
  };

  const domainBadgeTooltip =
    domainAvailability === 'available'
      ? 'Domain is available and ready to use.'
      : domainAvailability === 'checking'
        ? 'Checking if this domain is available...'
        : domainAvailability === 'unavailable'
          ? 'This domain is already taken. Please choose another one.'
          : (domainValidationMessage ?? 'Set your company domain to continue.');

  return (
    <div className={styles.panel}>
      {onRequestHide ? (
        <div className={styles.panelHeader}>
          <LayoutTemplate size={15} className={styles.panelHeaderIcon} />
          <span className={styles.panelHeaderTitle}>Templates</span>
          <button
            type="button"
            className={styles.panelHeaderBtn}
            onClick={onRequestHide}
            aria-label="Hide templates sidebar"
            title="Hide"
          >
            {'<'}
          </button>
        </div>
      ) : null}

      <div className={styles.domainSection}>
        <div className={styles.domainLabel}>Company Domain</div>
        <div className={styles.domainRow}>
          <input
            type="text"
            className={styles.domainInput}
            value={companyDomain}
            onChange={(e) => onChangeCompanyDomain(e.target.value)}
            placeholder="your-domain-name"
          />
          <span className={styles.domainSuffix}>/slug</span>
          <span
            className={`${styles.domainBadge} ${
              domainAvailability === 'available'
                ? styles.domainBadgeAvailable
                : domainAvailability === 'unavailable'
                  ? styles.domainBadgeUnavailable
                  : styles.domainBadgeNeutral
            }`}
            title={domainBadgeTooltip}
          >
            {domainAvailability === 'available' ? (
              <>
                <Check size={12} />
                Available
              </>
            ) : domainAvailability === 'checking' ? (
              'Checking...'
            ) : domainAvailability === 'unavailable' ? (
              'Unavailable'
            ) : (
              'Required'
            )}
          </span>
        </div>
        <div className={styles.domainHint}>
          URL: /www/{companyDomain.trim().toLowerCase() || 'your-domain'}/slug
        </div>
        {domainValidationMessage ? (
          <div className={styles.domainError}>{domainValidationMessage}</div>
        ) : null}
      </div>

      {/* Page Background */}
      <div className={styles.treeSection}>
        <div className={styles.categoryHeader}>
          <span className={styles.categoryTitle}>Page Background</span>
        </div>
        <div className={styles.categoryBody}>
          <div className={styles.pageBackgroundRow}>
            <input
              type="text"
              className={styles.pageBackgroundInput}
              value={pageBackgroundUrl}
              onChange={(e) => onChangePageBackgroundUrl(e.target.value)}
              placeholder="Background image URL"
              disabled={!domainReady}
            />
            <button
              className={styles.miniBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={!domainReady}
            >
              Browse
            </button>
            <button
              className={styles.miniBtn}
              onClick={() => onChangePageBackgroundUrl('')}
              disabled={!domainReady || !pageBackgroundUrl}
            >
              Clear
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleBackgroundFileChange}
            disabled={!domainReady}
          />
        </div>
      </div>

      {/* Sections Library */}
      <div className={styles.treeSection}>
        <div className={styles.categoryHeader}>
          <span className={styles.categoryTitle}>Sections Library</span>
        </div>
        <div className={styles.treeBody}>
          {(Object.keys(templatesByCategory) as SectionCategory[]).map(
            (cat) => {
              const temps = templatesByCategory[cat];
              if (temps.length === 0) return null;
              const isExpanded = expandedCategories.has(cat);
              return (
                <div key={cat} className={styles.treeCategory}>
                  <button
                    className={styles.treeCategoryToggle}
                    onClick={() => toggleCategory(cat)}
                  >
                    <ChevronRight
                      size={15}
                      className={`${styles.caret} ${isExpanded ? styles.caretRotated : ''}`}
                    />
                    <span>{cat}</span>
                  </button>
                  {isExpanded && (
                    <ul className={styles.templateTreeList}>
                      {temps.map((t) => (
                        <li key={t.key} className={styles.templateTreeItem}>
                          <div className={styles.templateItemInner}>
                            <span className={styles.templateName}>
                              {t.label}
                            </span>
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`t-${t.key}`}>
                                  {t.description}
                                </Tooltip>
                              }
                            >
                              <span className={styles.infoIcon}>
                                <Info size={13} />
                              </span>
                            </OverlayTrigger>
                          </div>
                          <button
                            className={styles.addBtnCircle}
                            onClick={() => onAddSection(t.key)}
                            aria-label={`Add ${t.label}`}
                            disabled={!domainReady}
                          >
                            <Plus size={16} strokeWidth={3} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            },
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplatePanel;
